import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  BookOpen,
  Video,
  Loader2,
  Search,
  Copy,
  Check,
  Trash2,
  Pencil,
  Upload,
  Play,
  X,
  EyeOff,
  ExternalLink,
} from 'lucide-react';
import { TopBar } from '../components/TopBar';
import { useAuth } from '../hooks/useAuth';
import { guidesApi, uploadGuideVideo, type GuideDoc, type GuideVideo } from '../lib/api';

/**
 * Guides — the reference library agents read while they work.
 *
 *  • Documents: HTML/markdown guides, served from the function behind
 *    requireAgent() and rendered in a SANDBOXED iframe. The sandbox is
 *    load-bearing twice over: each document brings its own global CSS (the
 *    Hebrew support guide resets `*`), and its own scripts (collapsible
 *    sections). Without `allow-same-origin` the frame gets an opaque origin, so
 *    those scripts can run without any reach into the console.
 *
 *  • Videos: how-to recordings, uploaded here and shared with customers by
 *    link. See services/guide-videos.ts for the sharing model.
 */

type Tab = 'docs' | 'videos';

export function GuidesPage() {
  const { user, claims, signOut } = useAuth();
  const tenantCode = claims?.tenantCode ?? '';
  const isAdmin = claims?.role === 'tenant_admin' || claims?.role === 'super_admin';

  const [params, setParams] = useSearchParams();
  const tab: Tab = params.get('tab') === 'videos' ? 'videos' : 'docs';
  const setTab = (t: Tab) => setParams(t === 'docs' ? {} : { tab: t }, { replace: true });

  return (
    <div className="min-h-screen bg-gray-50">
      <TopBar tenantCode={tenantCode} userEmail={user?.email ?? undefined} onSignOut={signOut} />

      <div className="pt-10 h-screen flex flex-col overflow-hidden">
        <div className="flex items-center gap-1 border-b border-gray-200 bg-white px-4">
          <h1 className="text-sm font-semibold text-gray-900 me-4">Guides</h1>
          {([
            { key: 'docs' as const, label: 'Documents', Icon: BookOpen },
            { key: 'videos' as const, label: 'Videos', Icon: Video },
          ]).map(({ key, label, Icon }) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                tab === key
                  ? 'text-primary-700 border-primary-600'
                  : 'text-gray-600 border-transparent hover:bg-gray-50'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>

        {tab === 'docs' ? (
          <DocsTab />
        ) : (
          <VideosTab tenantCode={tenantCode} isAdmin={isAdmin} />
        )}
      </div>
    </div>
  );
}

// ── Documents ─────────────────────────────────────────────────────────────

function DocsTab() {
  const [docs, setDocs] = useState<GuideDoc[]>([]);
  const [slug, setSlug] = useState<string | null>(null);
  const [html, setHtml] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingDoc, setLoadingDoc] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    guidesApi
      .listDocs()
      .then((r) => {
        setDocs(r.items);
        setSlug((s) => s ?? r.items[0]?.slug ?? null);
      })
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!slug) return;
    setLoadingDoc(true);
    setHtml(null);
    guidesApi
      .getDoc(slug)
      .then((r) => setHtml(r.html))
      .catch((e) => setError(String(e)))
      .finally(() => setLoadingDoc(false));
  }, [slug]);

  return (
    <div className="flex-1 flex min-h-0">
      <aside className="w-64 flex-shrink-0 border-e border-gray-200 bg-white overflow-y-auto p-3">
        {loading ? (
          <div className="flex items-center gap-2 text-xs text-gray-500 p-2">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
          </div>
        ) : docs.length === 0 ? (
          // Documents are per-tenant; a tenant with none gets an explanation
          // rather than an empty rail next to an empty pane.
          <div className="text-xs text-gray-500 p-2 leading-relaxed">
            No documents for this brand yet.
            <span className="block mt-1 text-gray-400">
              Written guides are added by the platform team. The Videos tab is
              yours to fill.
            </span>
          </div>
        ) : (
          docs.map((d) => (
            <button
              key={d.slug}
              type="button"
              onClick={() => setSlug(d.slug)}
              className={`block w-full text-left px-3 py-2.5 mb-1 rounded border transition-colors ${
                slug === d.slug
                  ? 'bg-primary-50 border-primary-200'
                  : 'bg-white border-transparent hover:bg-gray-50'
              }`}
            >
              <div
                className={`text-sm ${slug === d.slug ? 'font-semibold text-primary-800' : 'font-medium text-gray-800'}`}
                dir="auto"
              >
                {d.title}
              </div>
              <div className="text-[11px] text-gray-500 mt-0.5 leading-snug">{d.subtitle}</div>
              <div className="text-[10px] text-gray-400 mt-1">
                {Math.round(d.bytes / 1024)} kB · updated {new Date(d.updatedAt).toLocaleDateString()}
              </div>
            </button>
          ))
        )}
        {error && <div className="text-xs text-red-600 p-2">{error}</div>}
      </aside>

      <main className="flex-1 min-w-0 bg-white">
        {!loading && docs.length === 0 && (
          <div className="text-center text-sm text-gray-500 py-20">
            <BookOpen className="w-10 h-10 mx-auto text-gray-300 mb-2" />
            No written guides for this brand yet.
          </div>
        )}
        {loadingDoc && (
          <div className="flex items-center gap-2 text-sm text-gray-500 p-6">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading guide…
          </div>
        )}
        {html && (
          <iframe
            // Keyed on the slug so switching documents remounts the frame
            // instead of leaving the previous document's scroll position and
            // expanded sections behind.
            key={slug ?? ''}
            title="guide"
            srcDoc={html}
            // No allow-same-origin: the documents ship their own scripts, and
            // an opaque origin means those scripts cannot touch the console's
            // storage, cookies or DOM. allow-popups lets external links open.
            sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
            className="w-full h-full border-0"
          />
        )}
      </main>
    </div>
  );
}

// ── Videos ────────────────────────────────────────────────────────────────

function formatDuration(sec: number): string {
  if (!sec || !Number.isFinite(sec)) return '';
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

function formatSize(bytes: number): string {
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`;
  return `${Math.round(bytes / 1024 / 1024)} MB`;
}

function VideosTab({ tenantCode, isAdmin }: { tenantCode: string; isAdmin: boolean }) {
  const [items, setItems] = useState<GuideVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<string | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [playing, setPlaying] = useState<GuideVideo | null>(null);
  const [editing, setEditing] = useState<GuideVideo | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!tenantCode) return;
    setLoading(true);
    guidesApi
      .listVideos(tenantCode)
      .then((r) => setItems(r.items))
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, [tenantCode]);

  useEffect(load, [load]);

  const categories = useMemo(
    () => [...new Set(items.map((v) => v.category).filter(Boolean))].sort(),
    [items]
  );

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((v) => {
      if (category && v.category !== category) return false;
      if (!q) return true;
      return (
        v.title.toLowerCase().includes(q) ||
        v.description.toLowerCase().includes(q) ||
        v.category.toLowerCase().includes(q)
      );
    });
  }, [items, search, category]);

  async function copyLink(v: GuideVideo) {
    try {
      await navigator.clipboard.writeText(v.shareUrl);
      setCopiedId(v.id);
      setTimeout(() => setCopiedId((c) => (c === v.id ? null : c)), 1600);
    } catch {
      window.prompt('Copy this link:', v.shareUrl);
    }
  }

  async function remove(v: GuideVideo) {
    if (!window.confirm(`Delete "${v.title}"?\n\nThe file is deleted too, so any link already sent to a customer stops working.`))
      return;
    try {
      await guidesApi.deleteVideo(tenantCode, v.id);
      setItems((prev) => prev.filter((x) => x.id !== v.id));
    } catch (e) {
      alert(`Delete failed: ${e}`);
    }
  }

  return (
    <div className="flex-1 min-h-0 overflow-y-auto p-5">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative">
          <Search className="w-3.5 h-3.5 text-gray-400 absolute start-2.5 top-1/2 -translate-y-1/2" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search videos…"
            className="ps-8 pe-3 py-1.5 text-sm bg-white border border-gray-300 rounded w-64"
          />
        </div>
        {categories.length > 0 && (
          <div className="flex flex-wrap gap-1">
            <button
              type="button"
              onClick={() => setCategory(null)}
              className={`px-2.5 py-1 text-xs rounded-full border ${
                category === null
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              All
            </button>
            {categories.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCategory(c)}
                className={`px-2.5 py-1 text-xs rounded-full border ${
                  category === c
                    ? 'bg-primary-600 text-white border-primary-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        )}
        <div className="ms-auto flex items-center gap-2">
          <span className="text-xs text-gray-500">
            {visible.length} of {items.length}
          </span>
          {isAdmin && (
            <button
              type="button"
              onClick={() => setUploadOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded hover:bg-primary-700"
            >
              <Upload className="w-4 h-4" /> Upload video
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading library…
        </div>
      ) : error ? (
        <div className="text-sm text-red-600">{error}</div>
      ) : visible.length === 0 ? (
        <div className="text-center text-sm text-gray-500 py-16">
          <Video className="w-10 h-10 mx-auto text-gray-300 mb-2" />
          {items.length === 0
            ? isAdmin
              ? 'No videos yet. Upload one to get started.'
              : 'No videos yet.'
            : 'No videos match that filter.'}
        </div>
      ) : (
        <div className="grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(280px,1fr))]">
          {visible.map((v) => (
            <div
              key={v.id}
              className={`bg-white border rounded-lg overflow-hidden flex flex-col ${
                v.active ? 'border-gray-200' : 'border-amber-300 bg-amber-50/40'
              }`}
            >
              <button
                type="button"
                onClick={() => setPlaying(v)}
                className="relative h-32 bg-gray-900 flex items-center justify-center group"
                title="Play"
              >
                <Play className="w-9 h-9 text-white/80 group-hover:text-white group-hover:scale-110 transition" />
                {v.durationSec > 0 && (
                  <span className="absolute bottom-1.5 end-1.5 text-[10px] text-white bg-black/70 rounded px-1.5 py-0.5">
                    {formatDuration(v.durationSec)}
                  </span>
                )}
                {!v.active && (
                  <span className="absolute top-1.5 start-1.5 inline-flex items-center gap-1 text-[10px] text-amber-900 bg-amber-200 rounded px-1.5 py-0.5">
                    <EyeOff className="w-3 h-3" /> Link disabled
                  </span>
                )}
              </button>

              <div className="p-3 flex-1 flex flex-col">
                <div className="text-sm font-medium text-gray-900 leading-snug" dir="auto">
                  {v.title}
                </div>
                {v.description && (
                  <div className="text-xs text-gray-600 mt-1 line-clamp-2" dir="auto">
                    {v.description}
                  </div>
                )}
                <div className="flex items-center gap-2 mt-2 text-[11px] text-gray-500">
                  {v.category && (
                    <span className="bg-gray-100 rounded px-1.5 py-0.5">{v.category}</span>
                  )}
                  <span>{formatSize(v.size)}</span>
                  <span>· {v.views} view{v.views === 1 ? '' : 's'}</span>
                </div>

                <div className="flex items-center gap-1.5 mt-3 pt-2.5 border-t border-gray-100">
                  <button
                    type="button"
                    onClick={() => copyLink(v)}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-primary-700 bg-primary-50 rounded hover:bg-primary-100"
                    title="Copy the customer-facing link"
                  >
                    {copiedId === v.id ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    {copiedId === v.id ? 'Copied' : 'Copy link'}
                  </button>
                  <a
                    href={v.shareUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-600 rounded hover:bg-gray-100"
                    title="Open the link the customer sees"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                  {isAdmin && (
                    <>
                      <button
                        type="button"
                        onClick={() => setEditing(v)}
                        className="ms-auto inline-flex items-center px-2 py-1 text-xs text-gray-600 rounded hover:bg-gray-100"
                        title="Edit"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => remove(v)}
                        className="inline-flex items-center px-2 py-1 text-xs text-red-600 rounded hover:bg-red-50"
                        title="Delete"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {playing && <PlayerModal video={playing} onClose={() => setPlaying(null)} />}
      {uploadOpen && (
        <UploadModal
          tenantCode={tenantCode}
          categories={categories}
          onClose={() => setUploadOpen(false)}
          onDone={(v) => {
            setItems((prev) => [v, ...prev]);
            setUploadOpen(false);
          }}
        />
      )}
      {editing && (
        <EditModal
          tenantCode={tenantCode}
          video={editing}
          onClose={() => setEditing(null)}
          onSaved={(patch) => {
            setItems((prev) => prev.map((x) => (x.id === editing.id ? { ...x, ...patch } : x)));
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function PlayerModal({ video, onClose }: { video: GuideVideo; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-black rounded-lg overflow-hidden max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-3 py-2 bg-gray-900 text-gray-100">
          <span className="text-sm font-medium truncate" dir="auto">
            {video.title}
          </span>
          <button type="button" onClick={onClose} className="p-1 hover:bg-gray-800 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>
        {/* Plays through the same public share URL the customer gets, so a
            broken link is visible here rather than only in the customer's
            hands. */}
        <video src={video.shareUrl} controls autoPlay className="w-full max-h-[75vh] bg-black" />
      </div>
    </div>
  );
}

const MAX_VIDEO_MB = 500;

function UploadModal({
  tenantCode,
  categories,
  onClose,
  onDone,
}: {
  tenantCode: string;
  categories: string[];
  onClose: () => void;
  onDone: (v: GuideVideo) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [durationSec, setDurationSec] = useState(0);
  const [pct, setPct] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  function pick(f: File | null) {
    setError(null);
    if (!f) return;
    if (!/^video\/(mp4|webm|quicktime)$/.test(f.type)) {
      setError(`Unsupported format (${f.type || 'unknown'}). Use MP4, WebM or MOV.`);
      return;
    }
    if (f.size > MAX_VIDEO_MB * 1024 * 1024) {
      setError(`Too large: ${Math.round(f.size / 1024 / 1024)} MB. The limit is ${MAX_VIDEO_MB} MB.`);
      return;
    }
    setFile(f);
    if (!title) setTitle(f.name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' '));
    // Duration is only knowable client-side; the server never decodes the file.
    const url = URL.createObjectURL(f);
    const probe = document.createElement('video');
    probe.preload = 'metadata';
    probe.onloadedmetadata = () => {
      setDurationSec(Math.round(probe.duration) || 0);
      URL.revokeObjectURL(url);
    };
    probe.onerror = () => URL.revokeObjectURL(url);
    probe.src = url;
  }

  async function submit() {
    if (!file || !title.trim()) return;
    setError(null);
    setPct(0);
    try {
      const v = await uploadGuideVideo(
        tenantCode,
        file,
        { title: title.trim(), description, category, durationSec },
        setPct
      );
      onDone(v);
    } catch (e) {
      setError(String(e));
      setPct(null);
    }
  }

  const busy = pct !== null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h2 className="text-sm font-semibold text-gray-900">Upload a guide video</h2>
          <button type="button" onClick={onClose} disabled={busy} className="p-1 hover:bg-gray-100 rounded disabled:opacity-40">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="w-full border-2 border-dashed border-gray-300 rounded-lg py-6 text-sm text-gray-600 hover:border-primary-400 hover:bg-primary-50/40 disabled:opacity-50"
          >
            {file ? (
              <>
                <div className="font-medium text-gray-800">{file.name}</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {formatSize(file.size)}
                  {durationSec > 0 && ` · ${formatDuration(durationSec)}`}
                </div>
              </>
            ) : (
              <>
                <Upload className="w-6 h-6 mx-auto mb-1.5 text-gray-400" />
                Choose a video — MP4, WebM or MOV, up to {MAX_VIDEO_MB} MB
              </>
            )}
          </button>
          <input
            ref={inputRef}
            type="file"
            accept="video/mp4,video/webm,video/quicktime"
            className="hidden"
            onChange={(e) => pick(e.target.files?.[0] ?? null)}
          />

          <label className="block">
            <span className="text-xs font-medium text-gray-700">Title</span>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={busy}
              dir="auto"
              className="mt-1 w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded"
              placeholder="How to activate a new line"
            />
          </label>

          <label className="block">
            <span className="text-xs font-medium text-gray-700">Category (optional)</span>
            <input
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              disabled={busy}
              list="guide-video-categories"
              dir="auto"
              className="mt-1 w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded"
              placeholder="Activation"
            />
            <datalist id="guide-video-categories">
              {categories.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </label>

          <label className="block">
            <span className="text-xs font-medium text-gray-700">Description (optional)</span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={busy}
              rows={2}
              dir="auto"
              className="mt-1 w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded resize-none"
              placeholder="Shown to agents only — customers just get the video."
            />
          </label>

          {busy && (
            <div>
              <div className="h-2 bg-gray-200 rounded overflow-hidden">
                <div className="h-full bg-primary-600 transition-all" style={{ width: `${pct}%` }} />
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {pct! < 100 ? `Uploading… ${pct}%` : 'Finishing up…'}
              </div>
            </div>
          )}
          {error && <div className="text-xs text-red-600">{error}</div>}
        </div>

        <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!file || !title.trim() || busy}
            className="px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded hover:bg-primary-700 disabled:opacity-40"
          >
            {busy ? 'Uploading…' : 'Upload'}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditModal({
  tenantCode,
  video,
  onClose,
  onSaved,
}: {
  tenantCode: string;
  video: GuideVideo;
  onClose: () => void;
  onSaved: (patch: Partial<GuideVideo>) => void;
}) {
  const [title, setTitle] = useState(video.title);
  const [description, setDescription] = useState(video.description);
  const [category, setCategory] = useState(video.category);
  const [active, setActive] = useState(video.active);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setSaving(true);
    setError(null);
    const patch = { title: title.trim(), description, category, active };
    try {
      await guidesApi.updateVideo(tenantCode, video.id, patch);
      onSaved(patch);
    } catch (e) {
      setError(String(e));
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h2 className="text-sm font-semibold text-gray-900">Edit video</h2>
          <button type="button" onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <label className="block">
            <span className="text-xs font-medium text-gray-700">Title</span>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              dir="auto"
              className="mt-1 w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-gray-700">Category</span>
            <input
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              dir="auto"
              className="mt-1 w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded"
            />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-gray-700">Description</span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              dir="auto"
              className="mt-1 w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded resize-none"
            />
          </label>
          <label className="flex items-start gap-2 text-sm text-gray-800">
            <input
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              className="mt-1"
            />
            <span>
              Share link works
              <span className="block text-xs text-gray-500">
                Unchecking it kills the link immediately — including for customers who already have it.
              </span>
            </span>
          </label>
          {error && <div className="text-xs text-red-600">{error}</div>}
        </div>
        <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || !title.trim()}
            className="px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded hover:bg-primary-700 disabled:opacity-40"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
